import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { DefaultDropdown } from "../../../../_helpers/components/Forms/Dropdown";
import { InputForm } from "../../../../_helpers/components/Forms/InputLabel";
import { getHourTypes } from "../../../../_api/HourType";
import { askValidation } from "../../../../_api/Validation";
import { Button } from "../../../../_helpers/components/Button";
import { Toggle } from "../../../../_helpers/components/Forms/ToggleButton";
import { getPlanning } from "../../../../_api/TimeSlot";
import { getUserCounters } from "../../../../_api/Counter";
import toast from "react-hot-toast";
import { dateToAAAAMMJJ, floatRoundToString } from "../../../../_helpers/functions";
import { HourSlice } from "../../../../_helpers/components/Forms/HourSlice";

export const AskForm = (props) => {
    const { closeModal, refresh } = props;
    const [user, setUser] = useOutletContext();

    const CORRECTION_PLANNING_VALUE = "correction_planning";

    const [hoursType, setHoursType] = useState("");
    const [hoursTypeOptions, setHoursTypeOptions] = useState("");
    const [hourTypesForSlices, setHourTypesForSlices] = useState([]);

    const [comments, setComments] = useState("");
    const [startDate, setStartDate] = useState(dateToAAAAMMJJ(new Date(), "-"));
    const [endDate, setEndDate] = useState(dateToAAAAMMJJ(new Date(), "-"));
    const [timeslots, setTimeslots] = useState([]);
    const [selectedTS, setSelectedTS] = useState([]);

    const [correctionDay, setCorrectionDay] = useState(dateToAAAAMMJJ(new Date(), "-"));
    const [correctionTimeslots, setCorrectionTimeslots] = useState([]);
    const [isLoadingCorrectionTimeslots, setIsLoadingCorrectionTimeslots] = useState(false);
    const [correctionRows, setCorrectionRows] = useState([
        { hourTypeId: "", startTime: "09:00", endTime: "12:00" },
    ]);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const isCorrectionRequest = hoursType === CORRECTION_PLANNING_VALUE;

    const [userCountersOpen, setUserCountersOpen] = useState(false);
    const [userCounters, setUserCounters] = useState([]);
    const [userCountersLoading, setUserCountersLoading] = useState(false);
    const [userCountersLoadedForUserId, setUserCountersLoadedForUserId] = useState(null);

    const formatCounterValue = (rawValue, rawType) => {
        const type = `${rawType ?? ""}`.toUpperCase();
        const value = Number.parseFloat(rawValue);
        if (!Number.isFinite(value)) return "-";

        if (type === "DAYS") {
            return `${floatRoundToString(value, 3)}j`;
        }

        if (type === "HOURS") {
            const sign = value < 0 ? "-" : "";
            const abs = Math.abs(value);
            const totalMinutes = Math.round(abs * 60);
            const h = Math.floor(totalMinutes / 60);
            const m = totalMinutes % 60;
            return m === 0 ? `${sign}${h}h` : `${sign}${h}h${String(m).padStart(2, "0")}`;
        }

        return floatRoundToString(value, 3);
    };

    useEffect(() => {
        if (!userCountersOpen) return;
        if (!user?.id) return;
        if (String(userCountersLoadedForUserId) === String(user.id)) return;

        let cancelled = false;
        setUserCountersLoading(true);

        getUserCounters(user.id)
            .then((resp) => {
                if (cancelled) return;
                setUserCounters(resp.data ?? []);
                setUserCountersLoadedForUserId(user.id);
            })
            .catch(() => {
                if (cancelled) return;
                setUserCounters([]);
                setUserCountersLoadedForUserId(user.id);
            })
            .finally(() => {
                if (cancelled) return;
                setUserCountersLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [userCountersOpen, user?.id, userCountersLoadedForUserId]);

    useEffect(() => {
        getHourTypes().then((resp) => {
            // seuls les types d'heure à valider sont affichés
            const validationTypes = resp.data
                .filter((ht) => ht.is_with_validation === true)
                .map((ht) => ({
                    value: ht.id,
                    label: "Demande de " + ht.name,
                }));
            setHoursTypeOptions([
                ...validationTypes,
                {
                    value: CORRECTION_PLANNING_VALUE,
                    label: "Demande de correction de planning",
                },
            ]);
            if (validationTypes.length) {
                setHoursType(validationTypes[0].value);
            }

            const ht_work = {
                name: "Présence",
                content: resp.data.filter((ht) => ht.is_absence !== true),
            };
            const ht_abs = {
                name: "Absence",
                content: resp.data.filter((ht) => ht.is_absence === true),
            };
            const sliceTypes = [ht_work, ht_abs];
            setHourTypesForSlices(sliceTypes);

            const firstHourTypeId =
                sliceTypes?.[0]?.content?.[0]?.id ??
                sliceTypes?.[0]?.id ??
                "";
            if (firstHourTypeId) {
                setCorrectionRows((prev) =>
                    prev.map((r) => (r.hourTypeId ? r : { ...r, hourTypeId: firstHourTypeId }))
                );
            }
        });
    }, []);

    const formatTimestamp = (dateStr, endOfDay = false) => {
        const date = new Date(dateStr);
        if (endOfDay) {
            date.setDate(date.getDate() + 1);
        }
        return date.getTime() / 1000;
    };

    const parseToDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

        if (typeof value === "string") {
            // Handle "YYYY-MM-DD" as local date (avoid UTC shift).
            if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return parseDayKeyToDate(value);
            }
            // Handle "YYYY-MM-DD HH:mm:ss(.SSS)" by converting to ISO-like.
            const normalized = value.includes(" ") ? value.replace(" ", "T") : value;
            const d = new Date(normalized);
            return Number.isNaN(d.getTime()) ? null : d;
        }

        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
    };

    const toLocalDay = (value) => {
        const d = parseToDate(value);
        if (!d) return null;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };

    const formatDate = (dateInput) => {
        const date = new Date(dateInput);
        const pad = (num) => (num < 10 ? "0" + num : num);
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
            date.getDate()
        )}`;
    };

    // const expectedPlanningStateForDate = (dateInput) => {
    //     const targetDay = toLocalDay(dateInput);
    //     if (!targetDay) return "PROVISIONAL";

    //     const lastRealRaw = user?.last_date_planning_real;
    //     if (!lastRealRaw) return "PROVISIONAL";

    //     const lastRealDay = toLocalDay(lastRealRaw);
    //     if (!lastRealDay) return "PROVISIONAL";

    //     return targetDay <= lastRealDay ? "REAL" : "PROVISIONAL";
    // };

    useEffect(() => {
        if (!isCorrectionRequest && areDatesValid()) {
            getPlanning(
                user.id,
                formatTimestamp(startDate),
                formatTimestamp(endDate, true)
            ).then((resp) => {
                const filtered = (resp.data ?? []);
                // const filtered = (resp.data ?? []).filter(
                //     (ts) => ts.state === expectedPlanningStateForDate(ts.start_date)
                // );
                setTimeslots(filtered);
                setSelectedTS(filtered.map((ts) => ts.id));
            });
        } else if (!isCorrectionRequest) {
            setTimeslots([]);
            setSelectedTS([]);
        }
    }, [startDate, endDate, isCorrectionRequest, user.id, user?.last_date_planning_real]);

    useEffect(() => {
        if (!isCorrectionRequest) {
            setCorrectionTimeslots([]);
            return;
        }
        if (!correctionDay || !user?.id) {
            setCorrectionTimeslots([]);
            return;
        }

        setIsLoadingCorrectionTimeslots(true);
        getPlanning(
            user.id,
            formatTimestamp(correctionDay),
            formatTimestamp(correctionDay, true)
        )
            .then((resp) => {
                const dayStart = new Date(correctionDay);
                dayStart.setHours(0, 0, 0, 0);
                const dayEnd = new Date(correctionDay);
                dayEnd.setHours(23, 59, 59, 999);

                const forDay = (resp.data ?? []);

                setCorrectionTimeslots(forDay);

                if (forDay.length > 0) {
                    setCorrectionRows(
                        forDay.map((ts) => ({
                            hourTypeId: ts.hour_type_id ?? ts.hour_type?.id ?? "",
                            startTime: formatDateHours(ts.start_date),
                            endTime: formatDateHours(ts.end_date),
                        }))
                    );
                } else {
                    const defaultHourTypeId =
                        hourTypesForSlices?.[0]?.content?.[0]?.id ??
                        hourTypesForSlices?.[0]?.id ??
                        "";
                    setCorrectionRows([{ hourTypeId: defaultHourTypeId, startTime: "09:00", endTime: "12:00" }]);
                }
            })
            .catch(() => {
                setCorrectionTimeslots([]);
            })
            .finally(() => {
                setIsLoadingCorrectionTimeslots(false);
            });
    }, [correctionDay, isCorrectionRequest, user?.id, user?.last_date_planning_real, hourTypesForSlices]);

    const areDatesValid = () => {
        return startDate && endDate && new Date(startDate) <= new Date(endDate);
    };

    const formatDateHours = (dateInput) => {
        const date = parseToDate(dateInput) ?? new Date(dateInput);
        const pad = (num) => (num < 10 ? "0" + num : num);
        return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    const formatDateDDMMYYYY = (dateInput) => {
        const date = parseToDate(dateInput) ?? new Date(dateInput);
        const pad = (num) => (num < 10 ? "0" + num : num);
        return `${pad(date.getDate())}/${pad(
            date.getMonth() + 1
        )}/${date.getFullYear()}`;
    };

    const addCorrectionRow = () => {
        const first = correctionRows[0];
        const defaultHourTypeId =
            first?.hourTypeId ??
            hourTypesForSlices?.[0]?.content?.[0]?.id ??
            hourTypesForSlices?.[0]?.id ??
            "";

        setCorrectionRows((prev) => {
            const last = prev[prev.length - 1];
            let defaultStartTime = "09:00";
            let defaultEndTime = "10:00";

            if (last?.endTime) {
                const [hours, minutes] = last.endTime.split(":").map(Number);
                defaultStartTime = `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
                const endDate = new Date();
                endDate.setHours(hours, minutes, 0, 0);
                endDate.setHours(endDate.getHours() + 1);
                defaultEndTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;
            }

            return [
                ...prev,
                {
                    hourTypeId: defaultHourTypeId,
                    startTime: defaultStartTime,
                    endTime: defaultEndTime,
                },
            ];
        });
    };

    const removeCorrectionRow = (index) => {
        setCorrectionRows((prev) => prev.filter((_, i) => i !== index));
    };

    const setCorrectionStartTime = (value, index) => {
        setCorrectionRows((prev) => prev.map((r, i) => (i === index ? { ...r, startTime: value } : r)));
    };

    const setCorrectionEndTime = (value, index) => {
        setCorrectionRows((prev) => prev.map((r, i) => (i === index ? { ...r, endTime: value } : r)));
    };

    const setCorrectionHourType = (value, index) => {
        setCorrectionRows((prev) => prev.map((r, i) => (i === index ? { ...r, hourTypeId: value } : r)));
    };

    const parseDayKeyToDate = (dayKey) => {
        if (!dayKey || typeof dayKey !== "string") return null;
        const [y, m, d] = dayKey.split("-").map((v) => parseInt(v, 10));
        if (!y || !m || !d) return null;
        const res = new Date(y, m - 1, d);
        if (Number.isNaN(res.getTime())) return null;
        return res;
    };

    const askLeave = () => {
        if (isSubmitting) return;

        if (isCorrectionRequest) {
            if (!correctionDay) {
                toast.error("Veuillez sélectionner un jour.");
                return;
            }
            if (!comments) {
                toast.error("Veuillez renseigner un commentaire.");
                return;
            }
            if (!correctionRows || correctionRows.length === 0) {
                toast.error("Veuillez ajouter au moins une tranche horaire.");
                return;
            }

            const baseDayDate = parseDayKeyToDate(correctionDay);
            if (!baseDayDate) {
                toast.error("Jour invalide.");
                return;
            }

            const toTimestampSeconds = (hhmm, dayOffset = 0) => {
                if (!hhmm || typeof hhmm !== "string") return null;
                const [h, m] = hhmm.split(":").map((v) => parseInt(v, 10));
                if (Number.isNaN(h) || Number.isNaN(m)) return null;
                return new Date(
                    baseDayDate.getFullYear(),
                    baseDayDate.getMonth(),
                    baseDayDate.getDate() + dayOffset,
                    h,
                    m,
                    0,
                    0
                ).getTime() / 1000;
            };

            const validateRowTimes = (row) => {
                const startSeconds = toTimestampSeconds(row.startTime);
                let endSeconds = toTimestampSeconds(row.endTime);
                if (startSeconds === null || endSeconds === null) return null;

                // If end <= start, consider end on the next day (J+1).
                if (endSeconds <= startSeconds) {
                    endSeconds = toTimestampSeconds(row.endTime, 1);
                    if (endSeconds === null) return null;
                }

                if (endSeconds <= startSeconds) return null;
                return { startSeconds, endSeconds };
            };

            const validatedRows = [];
            for (const row of correctionRows) {
                if (!row.hourTypeId) {
                    toast.error("Type d'heure manquant pour un créneau.");
                    return;
                }
                const validated = validateRowTimes(row);
                if (!validated) {
                    toast.error("Horaires invalides.");
                    return;
                }
                validatedRows.push({ row, ...validated });
            }

            // Ensure no overlaps (touching is allowed).
            const intervals = validatedRows
                .map((v) => ({ startSeconds: v.startSeconds, endSeconds: v.endSeconds }))
                .sort((a, b) => a.startSeconds - b.startSeconds);

            for (let i = 1; i < intervals.length; i += 1) {
                if (intervals[i].startSeconds < intervals[i - 1].endSeconds) {
                    toast.error("Chevauchement détecté entre des créneaux.");
                    return;
                }
            }

            const payload = {
                hour_type_id: null,
                day: correctionDay,
                remarks: comments,
                rows: correctionRows.map((r) => ({
                    hour_type_id: r.hourTypeId,
                    start_time: r.startTime,
                    end_time: r.endTime,
                })),
            };

            setIsSubmitting(true);
            askValidation(payload)
                .then(() => {
                    refresh();
                    closeModal();
                })
                .catch((resp) => {
                    toast.error(resp.response?.data?.message || "Une erreur est survenue.");
                })
                .finally(() => {
                    setIsSubmitting(false);
                });
            return;
        }

        const payload = {
            type: hoursType,
            remarks: comments,
            start_date: startDate,
            end_date: endDate,
            timeslot_ids: selectedTS,
        };
        setIsSubmitting(true);
        askValidation(payload)
            .then(() => {
                refresh();
                closeModal();
            })
            .catch((resp) => {
                toast.error(resp.response?.data?.message || "Une erreur est survenue.");
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-3">
                    <DefaultDropdown
                        options={[].concat(hoursTypeOptions)}
                        name="Type de demande"
                        required={true}
                        value={hoursType}
                        onChange={(e) => setHoursType(e.target.value)}
                    />
                </div>
            </div>

            <div className="px-2 mb-3">
                <button
                    type="button"
                    onClick={() => setUserCountersOpen((v) => !v)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-800 hover:underline focus:outline-none"
                    aria-expanded={userCountersOpen}
                >
                    <span>Solde compteurs</span>
                    <svg
                        className={`w-4 h-4 transition-transform ${userCountersOpen ? "rotate-180" : ""}`}
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {userCountersOpen && (
                    <div className="mt-2">
                        {userCountersLoading ? (
                            <div className="text-xs text-gray-500">Chargement…</div>
                        ) : userCounters.length === 0 ? (
                            <div className="text-xs text-gray-500">Aucun compteur.</div>
                        ) : (
                            <div className="flex flex-wrap gap-1.5">
                                {userCounters.map((counter) => (
                                    <span
                                        key={counter.id}
                                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2 py-0.5 text-xs"
                                        title={counter.name}
                                    >
                                        <span className="max-w-56 truncate font-medium text-gray-700">
                                            {counter.name}
                                        </span>
                                        <span className="font-semibold tabular-nums text-gray-900">
                                            {formatCounterValue(counter.total_mvt, counter.type)}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {!isCorrectionRequest ? (
                <>
                    <div className="flex flex-wrap -mx-2">
                        <div className="w-full md:w-1/2 px-2 mb-3">
                            <InputForm
                                name="Début"
                                type="date"
                                required={true}
                                value={startDate}
                                min={formatDate(new Date())}
                                max={formatDate(
                                    new Date().setFullYear(new Date().getFullYear() + 2)
                                )}
                                set={setStartDate}
                            />
                        </div>
                        <div className="w-full md:w-1/2 px-2 mb-3">
                            <InputForm
                                name="Fin"
                                type="date"
                                required={true}
                                min={formatDate(new Date())}
                                max={formatDate(
                                    new Date().setFullYear(new Date().getFullYear() + 2)
                                )}
                                value={endDate}
                                set={setEndDate}
                            />{" "}
                        </div>
                    </div>
                    {areDatesValid() && (
                        <>
                            <hr /> <h4>Heures concernées :</h4>
                            <div className="flex flex-col space-y-2">
                                {timeslots.map((ts) => (
                                    <div
                                        key={ts.id}
                                        className="flex items-center space-x-2"
                                    >
                                        <span className="flex-1">
                                            {formatDateDDMMYYYY(ts.start_date)} -{" "}
                                            {ts.hour_type.name} :{" "}
                                            {formatDateHours(ts.start_date)} à{" "}
                                            {formatDateHours(ts.end_date)}
                                        </span>
                                        <Toggle
                                            enabled={selectedTS.includes(ts.id)}
                                            setEnabled={(val) => {
                                                if (val) {
                                                    setSelectedTS([
                                                        ...selectedTS,
                                                        ts.id,
                                                    ]);
                                                } else {
                                                    setSelectedTS(
                                                        selectedTS.filter(
                                                            (id) => id !== ts.id
                                                        )
                                                    );
                                                }
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            ) : (
                <>
                    <div className="flex flex-wrap -mx-2">
                        <div className="w-full px-2 mb-3">
                            <InputForm
                                name="Jour"
                                type="date"
                                required={true}
                                value={correctionDay}
                                min={formatDate(new Date().setDate(new Date().getDate() - 7))}
                                max={formatDate(
                                    new Date().setFullYear(new Date().getFullYear() + 2)
                                )}
                                set={setCorrectionDay}
                            />
                        </div>
                    </div>

                    <hr />
                    <div className="space-y-2">
                        <div className="text-sm font-semibold">Créneaux existants sur la journée :</div>
                        {isLoadingCorrectionTimeslots ? (
                            <div className="text-sm text-gray-500">Chargement…</div>
                        ) : correctionTimeslots.length === 0 ? (
                            <div className="text-sm text-gray-500">Aucun créneau sur la journée.</div>
                        ) : (
                            <div className="flex flex-col space-y-1">
                                {correctionTimeslots.map((ts) => (
                                    <div key={ts.id} className="text-sm text-gray-700">
                                        {ts.hour_type?.name ?? "Type inconnu"} : {formatDateHours(ts.start_date)} à{" "}
                                        {formatDateHours(ts.end_date)}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <hr />
                    <div className="space-y-6">
                        <div className="space-y-3">
                            {correctionRows.length === 0 ? (
                                <div className="text-sm text-gray-500">Aucun créneau sur la journée.</div>
                            ) : (
                                correctionRows.map((row, index) => (
                                    <>
                                        <HourSlice
                                            key={`correction-row-${index}`}
                                            index={index}
                                            nameStartTime={"Début"}
                                            nameEndTime={"Fin"}
                                            nameHourType={"Type d'heure"}
                                            listHourTypes={hourTypesForSlices}
                                            valueStartTime={row.startTime}
                                            valueEndTime={row.endTime}
                                            valueHourType={row.hourTypeId}
                                            setStartTime={setCorrectionStartTime}
                                            setEndTime={setCorrectionEndTime}
                                            setHourType={setCorrectionHourType}
                                            onRemove={correctionRows.length > 1 ? removeCorrectionRow : null}
                                            classNameHourType="w-full appearance-none w-60 bg-gray-200 border border-gray-200 text-gray-700 py-2 px-3 mr-2 rounded-sm leading-tight focus:outline-hidden focus:bg-white focus:border-gray-500 mb-2"
                                            className="col-span-1 appearance-none w-32 bg-gray-200 text-gray-700 border rounded-sm py-2 px-3 mr-2 leading-tight focus:outline-hidden focus:bg-white"
                                        />
                                    </>
                                ))
                            )}
                        </div>

                        <div className="flex items-center justify-between gap-3">
                            <Button
                                type="empty"
                                name="Ajouter un créneau"
                                action={() => {
                                    addCorrectionRow();
                                }}
                            />
                        </div>
                    </div>
                </>
            )}

            <hr />
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 mb-3">
                    <InputForm
                        name="Commentaires"
                        type="textarea"
                        required={true}
                        value={comments}
                        set={setComments}
                    />
                </div>
            </div>
            <div className="flex justify-end ml-2 mt-2">
                <div className="m-1">
                    <Button
                        id="add"
                        type={"empty"}
                        action={closeModal}
                        name="Annuler"
                    />
                </div>
                <div className="m-1">
                    <Button
                        id="add"
                        disabled={isSubmitting}
                        action={() => askLeave()}
                        name={
                            <span className="inline-flex items-center gap-2">
                                {isSubmitting && (
                                    <svg
                                        className="h-4 w-4 animate-spin"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z"
                                        />
                                    </svg>
                                )}
                                <span>{isSubmitting ? "Validation…" : "Valider"}</span>
                            </span>
                        }
                    />
                </div>
            </div>
        </>
    );
};
